import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { Check } from 'lucide-react';

import { useActiveSubject } from '../context/ActiveSubjectContext';
import { SubjectBadge } from './SubjectBadge';
import { SUBJECT_CODES, SUBJECT_CONFIG, type SubjectCode } from '../utils/subjectConfig';

type SubjectPickerButtonProps = {
  subject: SubjectCode;
  onSubjectChange: (subject: SubjectCode) => void;
  size?: 'sm' | 'md';
  showLabel?: boolean;
  menuAlign?: 'left' | 'right';
};

const MENU_WIDTH_PX = 176; // w-44
const MENU_VIEWPORT_PAD_PX = 8;

export function SubjectPickerButton({
  subject,
  onSubjectChange,
  size = 'md',
  showLabel = false,
  menuAlign = 'left',
}: SubjectPickerButtonProps) {
  const { pickerSubjects } = useActiveSubject();
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const subjectCodes = pickerSubjects.length ? pickerSubjects : SUBJECT_CODES;

  const closeMenu = useCallback(() => setOpen(false), []);

  const repositionMenu = useCallback(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    const rect = root.getBoundingClientRect();
    const preferredLeft = menuAlign === 'right'
      ? rect.right - MENU_WIDTH_PX
      : rect.left;
    const maxLeft = Math.max(
      MENU_VIEWPORT_PAD_PX,
      window.innerWidth - MENU_WIDTH_PX - MENU_VIEWPORT_PAD_PX,
    );
    const left = Math.min(Math.max(preferredLeft, MENU_VIEWPORT_PAD_PX), maxLeft);
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left,
      width: MENU_WIDTH_PX,
    });
  }, [menuAlign]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }
    repositionMenu();
  }, [open, repositionMenu]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        closeMenu();
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closeMenu();
      }
    }

    function onViewportChange() {
      repositionMenu();
    }

    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', onViewportChange, true);
    };
  }, [closeMenu, open, repositionMenu]);

  const handleSelect = useCallback((code: SubjectCode) => {
    closeMenu();
    if (code !== subject) {
      onSubjectChange(code);
    }
  }, [closeMenu, onSubjectChange, subject]);

  const currentConfig = SUBJECT_CONFIG[subject];

  return (
    <div ref={rootRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-2 rounded-md px-1 py-1 transition hover:bg-black/5"
        aria-label="切换学科"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <SubjectBadge subject={subject} size={size} />
        {showLabel ? (
          <span className="hidden text-sm font-semibold text-ink sm:inline">{currentConfig.label}</span>
        ) : null}
      </button>

      {open && menuStyle ? (
        <div
          role="listbox"
          aria-label="选择学科"
          style={menuStyle}
          className="z-50 overflow-hidden rounded-xl border border-border bg-surface shadow-card"
        >
          {subjectCodes.map((code) => {
            const config = SUBJECT_CONFIG[code];
            const isCurrent = code === subject;

            return (
              <button
                key={code}
                type="button"
                role="option"
                aria-selected={isCurrent}
                onClick={() => handleSelect(code)}
                className={`flex w-full items-center gap-2 px-3 py-2.5 text-left transition ${
                  isCurrent ? 'bg-primary/10' : 'hover:bg-canvas'
                }`}
              >
                <SubjectBadge subject={code} size="sm" />
                <span className="flex-1 text-sm font-semibold text-ink">{config.label}</span>
                {isCurrent ? <Check size={16} style={{ color: config.color }} /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
